import {
    DataTablePresenter,
    isTabletPortraitDown,
    ITableColumn,
    ProviderAccountRole,
    ProviderInvitation,
    ProviderUser,
    Spinner,
    ToastContext,
} from '@enter/core';
import { mdiArrowLeft, mdiChevronDown, mdiPlus } from '@mdi/js';
import { Icon } from '@mdi/react';
import _ from 'lodash';
import { inject, observer } from 'mobx-react';
import React, { useCallback, useContext, useEffect, useMemo, useRef } from 'react';
import { DialogContext } from 'services';
import { RootStore } from 'stores';
import { InviteMemberDialog } from './components';
import { InvitationRoleModal } from './invitation-role-modal/invitation-role-modal';
import { RoleModal } from './role-modal/role-modal';
import * as S from './shared.styles';

type IMemberData = ProviderUser | ProviderInvitation;

export const Members: React.FunctionComponent<{}> = inject('rootStore')(
    observer(
        (props: {}): React.ReactElement => {
            const rootStore: RootStore = _.get(props, 'rootStore');
            if (!rootStore) {
                return <></>;
            }
            const toastContext = useContext(ToastContext);
            const dialogContext = useContext(DialogContext);

            const { settingsStore } = rootStore;
            const { roles, users, invitations } = settingsStore;
            const currentUser = settingsStore.getCurrentUser(rootStore.userId);

            const uniqueInvitations = _.uniqBy(invitations, 'email');

            const members: IMemberData[] = _.compact(
                _.concat([], users as ProviderUser[], uniqueInvitations as ProviderInvitation[]),
            );
            const isUser = useCallback((member: IMemberData): boolean => _.includes(users, member), [users]);

            const abbr = (name?: string): string => _.toUpper(_.first(name));
            const isCurrentAdmin = !!currentUser && _.find(currentUser.roles, { roleId: 'PROVIDER_ADMIN_ACCESS' });

            const canEditUser = useCallback(
                (user: ProviderUser): boolean => {
                    return !!isCurrentAdmin && !!currentUser && !!user;
                },
                [isCurrentAdmin, currentUser],
            );

            const renderInvitation = useCallback(
                (member: IMemberData): React.ReactElement => (
                    <S.TableIconCell>
                        <S.TableIconCellInvitation />
                        <S.TableCell>
                            <S.TableCellBottom>{member.email}</S.TableCellBottom>
                        </S.TableCell>
                    </S.TableIconCell>
                ),
                [],
            );

            const renderUser = useCallback(
                (member: ProviderUser): React.ReactElement => (
                    <S.TableIconCell>
                        <S.TableIconCellProfile>{`${abbr(member.first)}${abbr(member.last)}`}</S.TableIconCellProfile>
                        <S.TableCell>
                            <S.TableCellTop>
                                {member.first} {member.last}
                            </S.TableCellTop>
                            <S.TableCellBottom>{member.email}</S.TableCellBottom>
                        </S.TableCell>
                    </S.TableIconCell>
                ),
                [],
            );

            const handleInviteNewMember = (): void => {
                dialogContext.showDialog({
                    props: {
                        onClose: handleInviteClose,
                        disableBackdropClick: true,
                        disableEscapeKeyDown: true,
                        renderTitle: 'Invite Team Members',
                    },
                    Component: (
                        <InviteMemberDialog
                            onClose={handleInviteClose}
                            accountId={rootStore.account?.grantedAccountId}
                        />
                    ),
                });
            };

            const handleInviteClose = (): void => {
                dialogContext.hideDialog();
            };

            const resendInvitation = useCallback(
                (invitation: ProviderInvitation): (() => void) => {
                    return async (): Promise<void> => {
                        if (invitation) {
                            const invitationsGroup = _.filter(
                                invitations,
                                (el: ProviderInvitation) => el.email === invitation.email,
                            );

                            const reqs: Promise<void>[] = [];

                            invitationsGroup.forEach(invitation => {
                                reqs.push(settingsStore.resendInvitation(invitation));
                            });

                            try {
                                await Promise.all(reqs);

                                toastContext.notify?.('Invitation resent');
                            } catch (err) {
                                dialogContext.showError(err);
                            }
                        }
                    };
                },
                [settingsStore, toastContext, invitations],
            );

            const removeInvitation = useCallback(
                (invitation: ProviderInvitation): (() => void) => {
                    return async (): Promise<void> => {
                        if (invitation) {
                            const invitationsGroup = _.filter(
                                invitations,
                                (el: ProviderInvitation) => el.email === invitation.email,
                            );

                            const reqs: Promise<void>[] = [];

                            invitationsGroup.forEach(invitation => {
                                reqs.push(settingsStore.removeInvitation(invitation));
                            });

                            try {
                                await Promise.all(reqs);

                                toastContext.notify?.('Invitation removed');
                            } catch (err) {
                                dialogContext.showError(err);
                            }
                        }
                    };
                },
                [settingsStore, toastContext, invitations],
            );

            const renderInvitationActions = useCallback(
                (invitation: ProviderInvitation): React.ReactElement[] => {
                    const role = _.find(roles, { roleId: invitation.roleId });
                    return [
                        <S.TableCellOption key="resend" onClick={resendInvitation(invitation)}>
                            Resend Invitation
                        </S.TableCellOption>,
                        <S.TableCellOption key="remove" onClick={removeInvitation(invitation)}>
                            Remove Invitation
                        </S.TableCellOption>,
                    ];
                },
                [isUser, resendInvitation, removeInvitation, roles],
            );

            const renderInvitationSelect = useCallback((): (() => React.ReactElement) => {
                return (): React.ReactElement => (
                    <S.InvitationSelectButton variant="primary">
                        <S.StyledIcon path={mdiChevronDown} size={1} />
                    </S.InvitationSelectButton>
                );
            }, []);

            const columns: ITableColumn<IMemberData>[] = useMemo(
                () => [
                    {
                        id: 'NameAndEmail',
                        Header: () => <S.TableHeader>Name & Email</S.TableHeader>,
                        Cell: ({ row }): React.ReactElement =>
                            isUser(row.original)
                                ? renderUser(row.original as ProviderUser)
                                : renderInvitation(row.original as ProviderInvitation),
                    },
                    {
                        id: 'Roles',
                        Header: () => <S.TableHeader>Roles</S.TableHeader>,
                        Cell: ({ row }): React.ReactElement => (
                            <S.TableCell>
                                {roles && checkIsUser(row.original) && (
                                    <S.RoleButton onClick={handleChangeRoles(row.original)}>Manage Roles</S.RoleButton>
                                )}
                                {roles && invitations && checkIsInvitation(row.original) && (
                                    <S.ManageContainer>
                                        <S.RoleButton onClick={handleChangeInvitationRoles(row.original)}>
                                            Manage Roles
                                        </S.RoleButton>
                                        <S.TableCellSelect
                                            renderButton={renderInvitationSelect()}
                                            selectable={checkIsUser(row.original)}
                                            pinned={checkIsUser(row.original)}>
                                            {canEditUser(row.original) ? renderInvitationActions(row.original) : <></>}
                                        </S.TableCellSelect>
                                    </S.ManageContainer>
                                )}
                            </S.TableCell>
                        ),
                    },
                ],
                [isUser, canEditUser, renderUser, renderInvitation, roles, invitations],
            );

            const presenter = useRef(
                new DataTablePresenter({
                    name: 'members',
                    data: members,
                    columns,
                    filterSchemas: [],
                }),
            ).current;

            useEffect(() => {
                presenter.onDataUpdate(members);
                presenter.onDataTotalChange(members.length);
            }, [presenter, members]);

            useEffect(() => {
                presenter.onColumnsUpdate(columns);
            }, [presenter, columns]);

            const handleChangeInvitationRoles = (invitation: ProviderInvitation) => {
                const invitationsGroup = _.filter(
                    invitations,
                    (el: ProviderInvitation) => el.email === invitation.email,
                );

                const handleCloseDialog = (): void => {
                    dialogContext.hideDialog?.();
                };

                const handleSubmit = async (newRoles: ProviderAccountRole[]) => {
                    const reqs: Promise<void>[] = [];

                    // Find invitations that need to be requested
                    const missingInvitations = newRoles.filter(
                        role => !invitationsGroup.find(el => el.accountId === role.accountId),
                    );

                    missingInvitations.forEach(el => {
                        if (!invitation.email) {
                            return;
                        }

                        const role = newRoles.find(role => role.roleId === el.roleId);

                        if (!role) {
                            return;
                        }

                        reqs.push(settingsStore.inviteMember(invitation.email, [role]));
                    });

                    invitationsGroup.forEach(invitation => {
                        const role = _.find(newRoles, role => role.accountId === invitation.accountId);

                        if (!role) {
                            return;
                        }

                        if (!role.roleId) {
                            reqs.push(settingsStore.removeInvitation(invitation));
                            return;
                        }

                        if (role.roleId !== invitation.roleId) {
                            reqs.push(
                                settingsStore.resendInvitation({
                                    ...invitation,
                                    roleId: role?.roleId || invitation.roleId,
                                }),
                            );
                        }
                    });
                    try {
                        await Promise.all(reqs);

                        handleCloseDialog();
                    } catch (err) {
                        dialogContext.showError(err);
                    }
                };

                const dialogProps = {
                    onClose: handleCloseDialog,
                    disableBackdropClick: true,
                    disableEscapeKeyDown: true,
                    renderTitle: `Manage roles access for ${invitation.email}`,
                };

                return () => {
                    dialogContext.showDialog?.({
                        props: dialogProps,
                        Component: (
                            <InvitationRoleModal
                                invitations={invitationsGroup}
                                accounts={rootStore.account ? _.compact(rootStore.account.accountAccess) : []}
                                roles={roles || []}
                                onClose={handleCloseDialog}
                                onSubmit={handleSubmit}
                            />
                        ),
                    });
                };
            };

            const handleChangeRoles = (member: ProviderUser) => {
                const handleCloseDialog = (): void => {
                    dialogContext.hideDialog?.();
                };

                const handleSubmit = async (newRoles: ProviderAccountRole[]) => {
                    await settingsStore.changeUserRole(member, _.compact(newRoles));

                    handleCloseDialog();
                };

                const dialogProps = {
                    onClose: handleCloseDialog,
                    disableBackdropClick: true,
                    disableEscapeKeyDown: true,
                    renderTitle: `Manage roles access for ${member.first} ${member.last}`,
                };

                return () => {
                    dialogContext.showDialog?.({
                        props: dialogProps,
                        Component: (
                            <RoleModal
                                user={member}
                                accounts={rootStore.account ? _.compact(rootStore.account.accountAccess) : []}
                                roles={roles || []}
                                onClose={handleCloseDialog}
                                onSubmit={handleSubmit}
                            />
                        ),
                    });
                };
            };

            return _.isEmpty(members) ? (
                <Spinner />
            ) : (
                <S.SubCategoryCol>
                    <S.SubCategoryItem key="members">
                        <S.SubCategoryItemRow>
                            <S.SubCategoryH1>
                                {isTabletPortraitDown() && (
                                    <S.ReturnLink to="/settings">
                                        {(): React.ReactElement => <Icon path={mdiArrowLeft} size={1} />}
                                    </S.ReturnLink>
                                )}
                                Members
                            </S.SubCategoryH1>
                            <S.SubCategoryAddButton variant="primary" onClick={handleInviteNewMember}>
                                <S.SubCategoryPlus path={mdiPlus} size={1} /> Invite new member
                            </S.SubCategoryAddButton>
                        </S.SubCategoryItemRow>
                        <S.Table presenter={presenter} hideFiltering={true} hidePagination={true} hideSettings={true} />
                        {/* <S.Table name="members" data={members} columns={columns} /> */}
                    </S.SubCategoryItem>
                </S.SubCategoryCol>
            );
        },
    ),
);

function checkIsUser(member: IMemberData): member is ProviderUser {
    return member['roleId'] === undefined;
}

function checkIsInvitation(member: IMemberData): member is ProviderInvitation {
    return member['roles'] === undefined;
}
